import { toast } from "@/components";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Switch } from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { routes } from "@/routes/utils";
import { useGetEmployeeGrid, useUpdateEmployeeMutation } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical, Edit } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const useEmployee = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [deletedRow] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showInActiveConfirmation, setShowInActiveConfirmation] =
    useState<boolean>(false);
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {
    data: employeeList,
    isLoading: isEmployeeLoading,
    refetch: refetchDoc,
    isRefetching: isEmployeeRefetching,
  } = useGetEmployeeGrid({ 
    data: {
      filters: [
        {
          field: "userType",
          value: "CUSTOMER",
          operator: "EQUALS",
        },
      ],
      sort: [
        {
          field: "createdAt",
          direction: "DESC",
        },
      ],
      pageNumber, 
      pageSize 
    }
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize]);

  const { mutate: updateEmployee, isPending: isUpdateEmployeePending } =
    useUpdateEmployeeMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
        toast({ message: "Status Updated Successfully" });
      },
    });
    
  // Define Table Columns
  const columns = [
    {
      accessorKey: "userId",
      header: "ID",
      accessorFn: (row: any) => String(row.userId),
      size: 100,
    },
    {
      accessorKey: "fullName",
      header: "Client Name",
      cell: ({ row }: any) => {
        return row.original.fullName ? (
          <span
            className="text-secondary border-b-1 border-secondary group-hover:border-secondary cursor-pointer font-[500]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`${row.original.userId}/view`);
            }}
          >
            {String(row.original.fullName)}
          </span>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
      size: 200,
    },
    {
      accessorKey: "email",
      header: <div className="w-full">Email ID</div>,
      accessorFn: (row: any) => String(row?.email || ""),
    },
    {
      accessorKey: "associatedProjects",
      header: <div className="w-full">Associate Projects</div>,
      cell: ({row}: any) => {
        return row?.original?.associatedProjects?.map((proj: any) => proj?.projectName)?.join(', ') || ""
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => {
        return (
          <div>
            <Switch
              onCheckedChange={() => {
                setSelectedUser(row.original);
                setTimeout(() => {
                  setShowInActiveConfirmation(true);
                }, 200);
              }}
              checked={row.original.status?.toLowerCase() == "active"}
            />
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({row}: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            <DropdownMenuItem
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`${row?.original?.userId}/edit`);
              }}
            >
              <Edit className="w-4 h-4" />
              Edit
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const onClose = () => {
    setOpenDialog(false);
  };

  const onAddClient = () => {
    navigate(`${routes.add}`);
  };

  const onActivateDeactivateEmployee = () => {
    updateEmployee({
      id: selectedUser.userId,
      data: {
        ...selectedUser,
        status:
          selectedUser.status?.toLowerCase() == "active"
            ? "Inactive"
            : "Active",
      },
    });
  };

  return {
    onAddClient,
    globalFilter,
    setGlobalFilter,
    columns,
    onClose,
    openDialog,
    employeeList,
    isEmployeeLoading,
    deletedRow,
    isMobile,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    isEmployeeRefetching,
    showInActiveConfirmation,
    setShowInActiveConfirmation,
    onActivateDeactivateEmployee,
    selectedUser,
    isUpdateEmployeePending
  };
};

export default useEmployee;
